import React, {useEffect, useRef, useState, useMemo} from 'react';
import {drawPath, constants, countryNames} from './WorldData';
import useMousePosition from './MouseTrack';
import {useId} from 'react';

interface CountryColorMap {
    [key: string]: string;
}

const hintStyleBase = {
    position: 'fixed' as React.CSSProperties['position'],
    backgroundColor: 'white',
    padding: '10px',
    borderRadius: '5px',
    border: '1px solid #ccc',
    pointerEvents: 'none' as React.CSSProperties['pointerEvents'],
    zIndex: 1000
};

type BorderStyle = 'solid' | 'dashed' | 'dotted' | 'dash-dot' | 'dash-double-dot';

const getStrokeProperties = (borderStyle?: BorderStyle) => {
    switch (borderStyle) {
        case 'dashed':
            return {strokeDasharray: '8 4'};
        case 'dotted':
            return {strokeDasharray: '2 2'};
        case 'dash-dot':
            return {strokeDasharray: '8 4 2 4'};
        case 'dash-double-dot':
            return {strokeDasharray: '8 4 2 4 2 4'};
        default:
            return {strokeDasharray: 'none'};
    }
};

export interface WorldMapProps {
    type: 'select-single' | 'select-multiple';
    size?: number;
    mapColor?: string;
    strokeColor?: string;
    strokeWidth?: number;
    hoverColor?: string;
    selectColor?: string;
    hints?: boolean;
    hintTextColor?: string;
    hintBorder?: string;
    hintBackgroundColor?: string;
    hintPadding?: string;
    hintBorderRadius?: string;
    onSelect?: (country: string) => void;
    onHover?: (country: string) => void;
    getTooltip?: (country: string) => React.ReactNode;
    countryColors?: CountryColorMap;
    disableClick?: boolean;
    disableHover?: boolean;
    borderStyle?: BorderStyle;
}

const WorldMap = ({
    size,
    mapColor,
    strokeColor,
    selectColor,
    strokeWidth,
    hoverColor,
    hints,
    onSelect,
    onHover,
    getTooltip,
    hintTextColor,
    hintBackgroundColor,
    hintPadding,
    hintBorder,
    hintBorderRadius,
    countryColors,
    disableClick,
    disableHover,
    borderStyle
}: WorldMapProps) => {
    const instanceId = useId().replace(/:/g, '');
    const {x, y} = useMousePosition();
    const [countryHovered, setCountryHovered] = useState<string | null>(null);
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [viewBox, setViewBox] = useState<string>('0 0 100 100');
    const svgRef = useRef<SVGSVGElement>(null);
    const strokeProps = useMemo(() => getStrokeProperties(borderStyle), [borderStyle]);

    // Fit the viewBox to the drawn paths. A dashboard can mount the map before it is attached and laid
    // out, when getBBox() is empty, so measure again on resize until the paths have a size.
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;

        const fitViewBox = () => {
            const bbox = svg.isConnected ? svg.getBBox() : null;
            if (!bbox?.width || !bbox?.height) return false;
            setViewBox(`${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
            return true;
        };

        if (fitViewBox()) return;
        const observer = new ResizeObserver(() => {
            if (fitViewBox()) observer.disconnect();
        });
        observer.observe(svg);
        return () => observer.disconnect();
    }, []);

    const mapStyle = useMemo(
        () => ({
            width: size,
            stroke: strokeColor,
            strokeWidth,
            ...strokeProps
        }),
        [size, strokeColor, strokeWidth, strokeProps]
    );

    const hintStyle = {
        ...hintStyleBase,
        backgroundColor: hintBackgroundColor || hintStyleBase.backgroundColor,
        padding: hintPadding || hintStyleBase.padding,
        border: hintBorder || hintStyleBase.border,
        borderRadius: hintBorderRadius || hintStyleBase.borderRadius,
        color: hintTextColor || 'black',
        top: y + 20,
        left: x + 20
    };

    useEffect(() => {
        countryNames.forEach(country => {
            const path = document.getElementById(`${country}-${instanceId}`);
            if (path) {
                path.style.fill = countryColors![country] || (mapColor as string);
            }
        });
    }, [countryColors, mapColor, instanceId]);

    useEffect(() => {
        if (selectedCountry) {
            const path = document.getElementById(`${selectedCountry}-${instanceId}`);
            if (path) {
                path.style.fill = selectColor || constants.SELECTED_COLOR;
            }
        }
    }, [selectedCountry, selectColor, instanceId]);

    const handleMouseEnter = (hoverCountryId: string) => {
        const path = document.getElementById(`${hoverCountryId}-${instanceId}`);
        setCountryHovered(hoverCountryId);
        if (path && !disableHover) {
            path.style.fill = selectedCountry === hoverCountryId ? selectColor || constants.SELECTED_COLOR : hoverColor || constants.HOVERCOLOR;
        }

        if (onHover) {
            onHover(hoverCountryId);
        }
    };

    const handleMouseLeave = (hoverCountryId: string) => {
        const path = document.getElementById(`${hoverCountryId}-${instanceId}`);
        setCountryHovered(null);
        if (path && !disableHover) {
            path.style.fill =
                selectedCountry === hoverCountryId ? selectColor || constants.SELECTED_COLOR : countryColors![hoverCountryId] || (mapColor as string);
        }
    };

    const handleClick = (countryCode: string) => {
        if (disableClick) return;

        if (selectedCountry === countryCode) {
            const path = document.getElementById(`${countryCode}-${instanceId}`);
            if (path) {
                path.style.fill = countryNames![countryCode] || (mapColor as string);
            }
            setSelectedCountry(null);
            if (onSelect) {
                onSelect(null);
            }
        } else {
            if (selectedCountry) {
                const previousPath = document.getElementById(`${selectedCountry}-${instanceId}`);
                if (previousPath) {
                    previousPath.style.fill = countryNames![selectedCountry] || (mapColor as string);
                }
            }
            setSelectedCountry(countryCode);
            if (onSelect) {
                onSelect(countryCode);
            }
        }
    };

    return (
        <>
            <div className="map" style={mapStyle}>
                <svg ref={svgRef} version="1.1" id={`svg2-${instanceId}`} x="0px" y="0px" viewBox={viewBox}>
                    {countryNames?.map((code, index) => (
                        <path
                            key={index}
                            onClick={() => handleClick(code)}
                            onMouseEnter={() => handleMouseEnter(code)}
                            onMouseLeave={() => handleMouseLeave(code)}
                            id={`${code}-${instanceId}`}
                            d={drawPath[code as keyof typeof drawPath]}
                            style={{
                                fill: countryColors![code] || mapColor,
                                cursor: disableClick ? 'default' : 'pointer',
                                ...strokeProps
                            }}
                        />
                    ))}
                </svg>
            </div>
            {hints && countryHovered && <div style={hintStyle}>{getTooltip ? getTooltip(countryHovered) : countryHovered}</div>}
        </>
    );
};

export default WorldMap;
